import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { trainingConcernsAPI, enrollmentsAPI } from '../services/api';
import Modal from '../components/Modal';
import SearchableSelect from '../components/Form/SearchableSelect';
import LoadingSpinner from '../components/LoadingSpinner';
import StatusBadge from '../components/StatusBadge';
import MessageDisplay from '../components/MessageDisplay';
import FilterBar from '../components/FilterBar/FilterBar';
import DataCard from '../components/DataCard/DataCard';
import FormField from '../components/Form/FormField';
import FormSelect from '../components/Form/FormSelect';
import FormTextarea from '../components/Form/FormTextarea';
import FormSection from '../components/Form/FormSection';
import { formatDate } from '../utils/formatUtils';
import useAsyncOperation from '../hooks/useAsyncOperation';
import { normalizeApiListResponse } from '../utils/apiListNormalize';
import StaffPageShell from '../components/StaffPageShell';

const TrainingConcernsPage = () => {
  const navigate = useNavigate();
  const [concerns, setConcerns] = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [filter, setFilter] = useState('all'); // all, open, in-progress, resolved, closed
  const { loading, error, success, execute, setError, setSuccess } = useAsyncOperation();
  const [typeFilter, setTypeFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingConcern, setEditingConcern] = useState(null);
  const [formData, setFormData] = useState({
    enrollmentId: '',
    concernType: 'General',
    description: '',
    issueDetails: '',
    resolutionStatus: 'Open',
    resolutionNotes: '',
    resolvedBy: ''
  });

  useEffect(() => {
    fetchConcerns();
    fetchEnrollments();
  }, []);

  const fetchEnrollments = async () => {
    try {
      const response = await enrollmentsAPI.getAll();
      setEnrollments(normalizeApiListResponse(response.data));
    } catch (err) {
      console.error('Error fetching enrollments:', err);
    }
  };

  const fetchConcerns = async () => {
    const response = await execute(async () => await trainingConcernsAPI.getAll());
    if (response?.data !== undefined) {
      setConcerns(normalizeApiListResponse(response.data));
    }
  };

  const handleCreate = () => {
    setEditingConcern(null);
    setFormData({
      enrollmentId: '',
      concernType: 'General',
      description: '',
      issueDetails: '',
      resolutionStatus: 'Open',
      resolutionNotes: '',
      resolvedBy: ''
    });
    setShowModal(true);
  };

  const handleEdit = (concern) => {
    setEditingConcern(concern);
    setFormData({
      enrollmentId: concern.enrollmentId?.toString() || '',
      concernType: concern.concernType || 'General',
      description: concern.description || '',
      issueDetails: concern.issueDetails || '',
      resolutionStatus: concern.resolutionStatus || 'Open',
      resolutionNotes: concern.resolutionNotes || '',
      resolvedBy: concern.resolvedBy || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const response = await execute(async () => {
      if (editingConcern) {
        return await trainingConcernsAPI.update(editingConcern.id, formData);
      } else {
        return await trainingConcernsAPI.create(formData);
      }
    }, { 
      successMessage: `Training concern ${editingConcern ? 'updated' : 'created'} successfully!` 
    });
    
    if (response) {
      setShowModal(false);
      setEditingConcern(null);
      fetchConcerns();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    const response = await execute(
      async () => await trainingConcernsAPI.delete(deleteConfirm.id),
      { successMessage: 'Training concern deleted successfully!' }
    );
    
    if (response) {
      setDeleteConfirm(null);
      fetchConcerns();
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleQuickResolve = async (concern) => {
    const userData = JSON.parse(localStorage.getItem('user'));
    const response = await execute(
      async () => await trainingConcernsAPI.update(concern.id, {
        resolutionStatus: 'Resolved',
        resolvedBy: userData?.user?.firstName || 'Admin',
        resolvedAt: new Date().toISOString()
      }),
      { successMessage: 'Concern marked as resolved!' }
    );
    
    if (response) {
      fetchConcerns();
      setTimeout(() => setSuccess(''), 3000);
    }
  };


  const getTypeColor = (type) => {
    switch (type) {
      case 'Fee':
        return 'bg-red-100 text-red-800';
      case 'Login':
        return 'bg-yellow-100 text-yellow-800';
      case 'Tool':
        return 'bg-purple-100 text-purple-800';
      case 'Delivery':
        return 'bg-blue-100 text-blue-800';
      case 'Module Repeat':
        return 'bg-indigo-100 text-indigo-800';
      case 'Placement':
        return 'bg-green-100 text-green-800';
      case 'Preparation':
        return 'bg-pink-100 text-pink-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredConcerns = concerns.filter(concern => {
    // Filter by status (only if filter is set and not 'all')
    if (filter && filter !== 'all' && concern.resolutionStatus !== filter) return false;

    // Filter by type (only if typeFilter is set and not 'all')
    if (typeFilter && typeFilter !== 'all' && concern.concernType !== typeFilter) return false;

    // Filter by search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const studentName = concern.enrollment?.student?.fullName?.toLowerCase() || '';
      const description = concern.description?.toLowerCase() || '';
      const batchName = concern.enrollment?.batch?.batchName?.toLowerCase() || '';
      if (!studentName.includes(term) && !description.includes(term) && !batchName.includes(term)) {
        return false;
      }
    }

    return true;
  });


  const concernTypes = ['Fee', 'Login', 'Tool', 'Delivery', 'Module Repeat', 'Placement', 'Preparation', 'General'];
  const statusTypes = ['Open', 'In Progress', 'Resolved', 'Closed'];

  return (
    <StaffPageShell title="Training Concerns" wide>
        <div className="flex justify-end items-center mb-6">
          <button
            onClick={handleCreate}
            className="px-6 py-2 rounded-lg font-medium theme-button-primary flex items-center gap-2"
          >
            <span>+</span> Add Concern
          </button>
        </div>

        <MessageDisplay message={error || success} />

        {/* Filters */}
        <FilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Search by student name, description, or batch..."
          filters={[
            {
              name: 'filter',
              label: 'Filter by Status',
              value: filter,
              onChange: setFilter,
              type: 'select',
              options: [{ value: 'all', label: 'All Statuses' }, ...statusTypes.map(status => ({ value: status, label: status }))]
            },
            {
              name: 'typeFilter',
              label: 'Filter by Type',
              value: typeFilter,
              onChange: setTypeFilter,
              type: 'select',
              options: [{ value: 'all', label: 'All Types' }, ...concernTypes.map(type => ({ value: type, label: type }))]
            }
          ]}
        />

        {loading && !concerns.length ? (
          <LoadingSpinner message="Loading concerns..." />
        ) : filteredConcerns.length === 0 ? (
          <div className="text-center py-12 theme-card rounded-lg">
            <p className="text-lg theme-text-secondary">No concerns found matching your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredConcerns.map((concern) => {
              const actions = [];
              if (concern.resolutionStatus !== 'Resolved' && concern.resolutionStatus !== 'Closed') {
                actions.push({
                  label: 'Quick Resolve',
                  onClick: () => handleQuickResolve(concern),
                  variant: 'view',
                  icon: <span className="text-sm">✓</span>
                });
              }
              actions.push(
                { label: 'Edit', onClick: () => handleEdit(concern), variant: 'edit' },
                { label: 'Delete', onClick: () => setDeleteConfirm(concern), variant: 'delete' }
              );

              return (
                <DataCard
                  key={concern.id}
                  title={concern.enrollment?.student?.fullName || 'Unknown Student'}
                  badges={[
                    { label: concern.concernType, className: getTypeColor(concern.concernType) },
                    { label: concern.resolutionStatus, className: '' }
                  ]}
                  metadata={[
                    { label: 'Batch', value: concern.enrollment?.batch?.batchName || 'N/A' },
                    { label: 'Course', value: concern.enrollment?.batch?.course?.courseName || 'N/A' },
                    { label: 'Created', value: formatDate(concern.createdAt) },
                    { label: 'Resolved', value: concern.resolvedAt ? formatDate(concern.resolvedAt) : 'N/A' }
                  ]}
                  actions={actions}
                >
                  <div className="mt-4">
                    <div className="mb-2">
                      <span className="font-medium theme-text-secondary">Description:</span>
                      <p className="theme-text mt-1">{concern.description}</p>
                    </div>
                    {concern.issueDetails && (
                      <div className="mb-2">
                        <span className="font-medium theme-text-secondary">Issue Details:</span>
                        <p className="theme-text mt-1">{concern.issueDetails}</p>
                      </div>
                    )}
                    {concern.resolutionNotes && (
                      <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                        <span className="font-medium theme-text-secondary">Resolution Notes:</span>
                        <p className="theme-text mt-1">{concern.resolutionNotes}</p>
                        {concern.resolvedBy && (
                          <p className="text-xs theme-text-secondary mt-1">Resolved by: {concern.resolvedBy}</p>
                        )}
                      </div>
                    )}
                  </div>
                </DataCard>
              );
            })}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Modal
          isOpen={showModal}
          onClose={() => {
            setShowModal(false);
            setEditingConcern(null);
          }}
          title={editingConcern ? 'Edit Training Concern' : 'Add New Training Concern'}
          size="md"
        >
          <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block theme-text mb-1 font-medium">Student Enrollment *</label>
                    <SearchableSelect
                      required
                      value={formData.enrollmentId}
                      onChange={(e) => setFormData({ ...formData, enrollmentId: e.target.value })}
                      disabled={!!editingConcern}
                      className="w-full"
                      placeholder="Select Enrollment"
                      options={enrollments}
                      getOptionValue={(enrollment) => enrollment.id}
                      getOptionLabel={(enrollment) =>
                        `${enrollment.student?.fullName || 'Unknown'} - ${enrollment.batch?.batchName || 'N/A'}`
                      }
                    />
                    {editingConcern && (
                      <p className="text-xs text-gray-500 mt-1">Enrollment cannot be changed</p>
                    )}
                  </div>

                  <div>
                    <label className="block theme-text mb-1 font-medium">Concern Type *</label>
                    <SearchableSelect
                      required
                      value={formData.concernType}
                      onChange={(e) => setFormData({ ...formData, concernType: e.target.value })}
                      className="w-full"
                      options={concernTypes.map((type) => ({ value: type, label: type }))}
                    />
                  </div>

                  <div>
                    <label className="block theme-text mb-1 font-medium">Resolution Status *</label>
                    <SearchableSelect
                      required
                      value={formData.resolutionStatus}
                      onChange={(e) => setFormData({ ...formData, resolutionStatus: e.target.value })}
                      className="w-full"
                      options={statusTypes.map((status) => ({ value: status, label: status }))}
                    />
                  </div>
                </div>

                <div>
                  <label className="block theme-text mb-1 font-medium">Description *</label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg theme-input"
                    rows="3"
                    placeholder="Brief description of the concern..."
                  />
                </div>

                <div>
                  <label className="block theme-text mb-1 font-medium">Issue Details</label>
                  <textarea
                    value={formData.issueDetails}
                    onChange={(e) => setFormData({ ...formData, issueDetails: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg theme-input"
                    rows="4"
                    placeholder="Detailed information about the issue..."
                  />
                </div>

                {(formData.resolutionStatus === 'Resolved' || formData.resolutionStatus === 'Closed') && (
                  <div>
                    <label className="block theme-text mb-1 font-medium">Resolution Notes</label>
                    <textarea
                      value={formData.resolutionNotes}
                      onChange={(e) => setFormData({ ...formData, resolutionNotes: e.target.value })}
                      className="w-full px-3 py-2 border rounded-lg theme-input"
                      rows="3"
                      placeholder="Notes about how the concern was resolved..."
                    />
                    <div className="mt-2">
                      <label className="block theme-text mb-1 font-medium">Resolved By</label>
                      <input
                        type="text"
                        value={formData.resolvedBy}
                        onChange={(e) => setFormData({ ...formData, resolvedBy: e.target.value })}
                        className="w-full px-3 py-2 border rounded-lg theme-input"
                        placeholder="Name of person who resolved this"
                      />
                    </div>
                  </div>
                )}

                <div className="flex space-x-4 pt-4">
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-primary disabled:opacity-50"
                  >
                    {loading ? (editingConcern ? 'Updating...' : 'Creating...') : (editingConcern ? 'Update' : 'Create')}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      setEditingConcern(null);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
                  >
                    Cancel
                  </button>
                </div>
              </form>
        </Modal>

        {/* Delete Confirmation Modal */}
        <Modal
          isOpen={!!deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          title="Delete Training Concern"
          size="sm"
        >
          <p className="theme-text-secondary mb-4">
            Are you sure you want to delete this concern for <strong>{deleteConfirm?.enrollment?.student?.fullName || 'this student'}</strong>?
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            <strong>Type:</strong> {deleteConfirm?.concernType}<br />
            <strong>Description:</strong> {deleteConfirm?.description?.substring(0, 100)}...
          </p>
          <div className="flex space-x-4">
            <button
              onClick={handleDelete}
              disabled={loading}
              className="flex-1 px-4 py-2 rounded-lg font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? 'Deleting...' : 'Delete'}
            </button>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="flex-1 px-4 py-2 rounded-lg font-medium theme-button-secondary"
            >
              Cancel
            </button>
          </div>
        </Modal>
    </StaffPageShell>
  );
};

export default TrainingConcernsPage;
